import type { SanityQueryResponse } from './sanity-types';

/** Per-query transport options. Absent means a published, cacheable read. */
export interface SanityQueryOptions {
  /**
   * Read from the `drafts` perspective instead of the published one. Requires a
   * token; see `createSanityClientRunner`, which also stega-encodes these reads.
   */
  draft?: boolean;
}

/**
 * Transport seam for executing GROQ queries.
 *
 * The adapter core and the pure mappers depend only on this interface, so the
 * transport can be swapped without touching them:
 *   - `createFetchQueryRunner` (below) — dependency-free default.
 *   - a `@sanity/client`-backed runner (server-only) — injected in production
 *     once the dependency clears the security review.
 *   - a stub — injected in tests.
 */
export interface SanityQueryRunner {
  query<T>(groq: string, params: Record<string, unknown>, options?: SanityQueryOptions): Promise<T>;
}

export interface FetchQueryRunnerConfig {
  projectId: string;
  dataset: string;
  /** Sanity API version, e.g. `2024-01-01`. */
  apiVersion?: string;
  /** Read token for private datasets. Omit for public datasets. */
  token?: string;
  /**
   * Use the CDN host (`apicdn`) for cached reads. Defaults to `true`, or to `false` when a
   * `token` is set — `apicdn` cannot serve authenticated or draft reads, so a token'd request
   * routed there comes back as public, published content with no error to say so.
   */
  useCdn?: boolean;
  /** Override the API base host (local proxy / mock / region). */
  apiHost?: string;
  /** Injectable for tests — defaults to global `fetch`. */
  fetchImpl?: typeof fetch;
}

/**
 * Default runner: talks to Sanity's HTTP query API via `fetch`, no SDK.
 *
 * NOTE: this puts the GROQ query in the URL (GET). Fine for small projections;
 * large ones can exceed URL-length limits — the `@sanity/client` runner (which
 * switches to POST automatically) supersedes this in production.
 */
export const createFetchQueryRunner = (config: FetchQueryRunnerConfig): SanityQueryRunner => {
  const fetchImpl = config.fetchImpl ?? fetch;
  const apiVersion = config.apiVersion ?? 'v2024-01-01';
  // Matches `sanity-client-runner.ts`'s default. Diverging here is a silent-wrong-answer bug: the
  // CDN would serve published content for an authenticated read instead of failing.
  const useCdn = config.useCdn ?? !config.token;

  const queryUrl = (groq: string, params: Record<string, unknown>, draft: boolean): URL => {
    const host =
      config.apiHost?.replace(/\/$/, '') ??
      // A drafts read can never go to `apicdn`, whatever `useCdn` says.
      `https://${config.projectId}.${useCdn && !draft ? 'apicdn' : 'api'}.sanity.io`;
    const version = apiVersion.startsWith('v') ? apiVersion : `v${apiVersion}`;
    const url = new URL(`${host}/${version}/data/query/${config.dataset}`);
    url.searchParams.set('query', groq);
    if (draft) url.searchParams.set('perspective', 'drafts');
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(`$${key}`, JSON.stringify(value));
    }
    return url;
  };

  return {
    query: async <T>(groq: string, params: Record<string, unknown>, options?: SanityQueryOptions): Promise<T> => {
      const draft = options?.draft === true;
      // Same silent-wrong-answer guard as the SDK runner: an unauthenticated drafts
      // read comes back as published content with no error to say so.
      if (draft && !config.token) {
        throw new Error(
          'Sanity draft mode requires SANITY_API_TOKEN (a Viewer token). Without it the drafts perspective silently returns published content.',
        );
      }
      const response = await fetchImpl(queryUrl(groq, params, draft), {
        headers: config.token ? { Authorization: `Bearer ${config.token}` } : {},
      });
      if (!response.ok) {
        throw new Error(`Sanity query API responded ${response.status}`);
      }
      const body = (await response.json()) as SanityQueryResponse<T>;
      return body.result;
    },
  };
};
